import { cn } from "@gigpro/ui/tailwind/cn";
import { Tooltip } from "@gigpro/ui/Tooltip";
import { Link } from "@tanstack/react-router";
import type { NavItemButton, NavItemLink } from "layouts/BaseLayout/types";
import { useDoesPathMatch, useNavLinkProps } from "layouts/BaseLayout/utils";
import type React from "react";

export const sidebarRowClassName = (collapsed: boolean) =>
  cn(
    "flex w-full items-center gap-3 rounded-sm border-l-2 border-transparent py-2 text-normal font-semibold whitespace-nowrap text-white transition-colors hover:bg-white/10 data-active:border-brand data-active:bg-white/15 [&>svg]:size-4 [&>svg]:shrink-0",
    collapsed ? "justify-center px-0" : "px-2.5",
  );

export const SidebarTooltip = ({
  collapsed,
  label,
  children,
}: React.PropsWithChildren<{ collapsed: boolean; label: string }>) =>
  collapsed ? (
    <Tooltip content={label} side="right">
      {children}
    </Tooltip>
  ) : (
    children
  );

type SidebarNavItemProps = {
  item: NavItemLink | NavItemButton;
  collapsed: boolean;
  onClick?: () => void;
};

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ item, collapsed, onClick }) => {
  const active = useDoesPathMatch(item.type === "link" ? item : { path: "", external: true });
  const navLinkProps = useNavLinkProps();

  const content = (
    <>
      {item.icon}
      {!collapsed && <span className="truncate">{item.title}</span>}
      {!collapsed && item.type === "link" && item.isBeta && (
        <span className="rounded-xl bg-tags-green px-2 text-small text-black">Beta</span>
      )}
    </>
  );

  return (
    <SidebarTooltip collapsed={collapsed} label={item.title}>
      {item.type === "link" ? (
        <Link
          {...navLinkProps(item)}
          target={item.external ? "_blank" : undefined}
          onClick={onClick}
          aria-label={collapsed ? item.title : undefined}
          data-active={active || undefined}
          className={sidebarRowClassName(collapsed)}
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => {
            item.onClick();
            onClick?.();
          }}
          aria-label={collapsed ? item.title : undefined}
          className={sidebarRowClassName(collapsed)}
        >
          {content}
        </button>
      )}
    </SidebarTooltip>
  );
};
